import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { computeAmount } from '@captaincause/lib/amount'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { DiftWithAmount } from '@captaincause/ui/Dift'
import { Ficello } from '@captaincause/ui/Ficello'
import { tm } from '@captaincause/ui/tm'
import { TYPES } from 'components/DiftType/types'
import type { Dift, Locales } from '@captaincause/types'

interface DiftHeaderProps {
  dift?: Dift
}

export function DiftHeader({ dift = undefined }: DiftHeaderProps) {
  const { t } = useTranslation('give-dift.page')
  const router = useRouter()
  const locale = useNextLocale()

  if (!dift) return null

  return (
    <section className="mx-auto my-6 w-full max-w-screen-2xl px-4 md:px-8 lg:px-10 xl:px-20">
      <div
        className={tm(
          'relative mx-auto flex flex-col justify-center overflow-hidden rounded-2xl bg-light-beige px-4 py-6 md:flex-row',
          TYPES[dift.type].colors.background,
          TYPES[dift.type].colors.text,
        )}
      >
        <Ficello
          variant="ressort"
          className={tm(
            'absolute left-[100px] md:-left-[700px] md:-top-[200px] lg:-left-[700px] lg:-top-[200px] xl:-left-[700px] xl:-top-[200px]',
            TYPES[dift.type].colors.ficelloStroke,
          )}
          rainbow={TYPES[dift.type].colors.ficelloStrokeRainbow}
        />
        <Ficello
          variant="ressort"
          className={tm(
            'absolute right-[100px] md:-right-[800px] md:-top-[200px] md:hidden lg:-right-[700px] lg:-top-[200px] xl:absolute xl:-right-[700px] xl:-top-[200px]',
            TYPES[dift.type].colors.ficelloStroke,
          )}
          rainbow={TYPES[dift.type].colors.ficelloStrokeRainbow}
        />

        {dift.amount.amount > 0 ? (
          <DiftWithAmount
            dift={dift}
            amount={dift.amount}
            locale={locale}
            className={tm(
              'absolute left-1/2 top-0 -translate-x-1/2 scale-75 md:left-[unset] md:right-4 md:top-4 md:-translate-y-1/4 md:translate-x-1/4 md:scale-50 lg:right-12 lg:top-10 lg:scale-75',
              TYPES[dift.type].colors.text,
            )}
            backgroundColor={TYPES[dift.type].colors.diftBackgroundColor}
          />
        ) : null}
        <div className="z-10 mt-28 flex w-full flex-col items-center justify-center md:mt-0">
          <h1 className="mt-4 w-full text-center font-default text-2xl font-semibold md:mt-0 md:max-w-[65%] md:text-3.5xl md:leading-10">
            <Trans
              t={t}
              i18nKey="header.title"
              values={{
                receiver: dift.receiver_name,
                sender: dift.sender_name,
              }}
              components={{
                b: <br />,
              }}
            />
          </h1>

          <h2 className="mt-4 w-full text-center font-default text-lg [&>b]:font-default">
            <Trans
              t={t}
              i18nKey="header.description"
              values={{
                amount: computeAmount(dift.amount, router.locale as Locales),
              }}
              components={{
                b: <br />,
              }}
            />
          </h2>

          {dift.message ? (
            <div
              className={tm(
                'mt-6 w-full max-w-screen-2xl rounded-2xl bg-white p-4 text-center shadow-sm md:w-fit lg:max-w-1/2',
                TYPES[dift.type].colors.diftBackground,
                TYPES[dift.type].colors.text,
              )}
            >
              <p className="line-clamp-6">&ldquo;{dift.message}&rdquo;</p>
              {dift.sender_name ? (
                <span className="mt-2 block font-semibold">
                  {dift.sender_name}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
